import React, { useState, useEffect } from 'react';
import { CalendarIcon, ChartPieIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { useUser } from '../contexts/UserContext';

const TicketCategoryBreakdown = () => {
  const { isAdmin } = useUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const userId = isAdmin() ? null : authService.getUserId();
        const teamsData = await apiService.getTeams(userId);
        setTeams(teamsData);
      } catch (err) {
        console.error('Failed to load teams:', err);
      }
    };
    loadTeams();
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await apiService.getTicketCategoryBreakdown(startDate, endDate, selectedTeamId);
      console.log('Category breakdown data:', result);
      setData(result);
    } catch (err) {
      console.error('Failed to load category breakdown:', err);
      setError('Failed to load report data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = () => {
    loadData();
  };

  const handleResetDates = () => {
    setStartDate(null);
    setEndDate(null);
    loadData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadData}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!data || data.categories.length === 0) {
    return (
      <div className="text-center py-12">
        <ChartPieIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No data available</h3>
        <p className="mt-1 text-sm text-gray-500">
          No tickets found for the selected date range.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Ticket Category Breakdown
          </h1>
          <p className="text-gray-600">
            Analyze ticket distribution across different categories
          </p>
        </div>
      </div>

      {/* Date Range Controls */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-wrap items-center gap-4">
          {teams.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">Team:</span>
              <select
                value={selectedTeamId ?? ''}
                onChange={(e) => setSelectedTeamId(e.target.value ? parseInt(e.target.value) : null)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Teams</option>
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Date Range:</span>
          </div>
          
          <input
            type="date"
            value={startDate ? startDate.toISOString().split('T')[0] : ''}
            onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          
          <span className="text-gray-500">to</span>
          
          <input
            type="date"
            value={endDate ? endDate.toISOString().split('T')[0] : ''}
            onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          
          <button
            onClick={handleDateChange}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Apply Filter
          </button>
          
          <button
            onClick={handleResetDates}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Reset
          </button>
        </div>
        
        {data.startDate || data.endDate ? (
          <div className="mt-3 text-sm text-gray-600">
            Showing data from{' '}
            {data.startDate ? new Date(data.startDate).toLocaleDateString() : 'beginning'} to{' '}
            {data.endDate ? new Date(data.endDate).toLocaleDateString() : 'now'}
            {' '}(Total: {data.totalTickets} tickets)
          </div>
        ) : (
          <div className="mt-3 text-sm text-gray-600">
            Showing all data (Total: {data.totalTickets} tickets)
          </div>
        )}
      </div>

      

      {/* Chart Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <ChartPieIcon className="h-6 w-6 text-blue-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Category Distribution Chart</h2>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                     {/* Pie Chart - Takes up 2/3 of the space */}
           <div className="lg:col-span-2 bg-gray-50 rounded-lg p-4">
             {data.categories && data.categories.length > 0 ? (
               <div style={{ width: '100%', height: '400px', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                 <PieChart width={400} height={400}>
                                       <Pie
                      data={data.categories}
                      cx={200}
                      cy={200}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="ticketCount"
                      nameKey="categoryName"
                      labelLine={false}
                    >
                     {data.categories.map((entry, index) => (
                       <Cell key={`cell-${index}`} fill={entry.color} />
                     ))}
                   </Pie>
                   <Tooltip formatter={(value, name, props) => [`${value} tickets`, props.payload.categoryName]} />
                 </PieChart>
               </div>
             ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <p>No category data available for chart</p>
              </div>
            )}
          </div>
          
          {/* Chart Legend - Takes up 1/3 of the space */}
          <div className="space-y-3">
            <h3 className="font-medium text-gray-900">Category Breakdown</h3>
            {data.categories.map((category, index) => (
              <div key={category.categoryId} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-4 h-4 rounded mr-3"
                    style={{ backgroundColor: category.color }}
                  ></div>
                  <span className="text-sm font-medium text-gray-900">
                    {category.categoryName}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-gray-900">
                    {category.ticketCount}
                  </div>
                  <div className="text-xs text-gray-500">
                    {category.percentage}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <TableCellsIcon className="h-6 w-6 text-blue-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Detailed Category Table</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ticket Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Percentage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visual
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.categories.map((category) => (
                <tr key={category.categoryId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded mr-3"
                        style={{ backgroundColor: category.color }}
                      ></div>
                      <span className="text-sm font-medium text-gray-900">
                        {category.categoryName}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {category.ticketCount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {category.percentage}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className="h-2 rounded-full"
                          style={{ 
                            width: `${category.percentage}%`,
                            backgroundColor: category.color 
                          }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500 w-12">
                        {category.percentage}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Report Metadata */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="text-sm text-gray-600 text-center">
          Report generated at {new Date(data.generatedAt).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default TicketCategoryBreakdown;
